import request from "@/utils/request";

export interface MenuDataItem {
  id: number;
  pid: number;
  name: string;
  nameEn?: string;
  address: string;
  icon: string;
  sort: number;
  visible: string;
  type: number; // 1页面 2按钮
  createdAt: number;
  updatedAt: number;
  children: MenuDataItem[] | null;
}

export interface CommonResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

export interface CreateMenuParams {
  pid: number;
  name: string;
  nameEn?: string;
  address: string;
  icon?: string;
  sort: number;
  visible?: string; // 适用主体多选: 1平台 2一级代理 3二级代理 4商户
  type: number; // 1页面 2按钮
}

export interface UpdateMenuParams extends CreateMenuParams {
  id: number;
}

export interface DeleteMenuParams {
  id: number;
}

export interface RoleDataItem {
  id: number;
  name: string;
  status: number; // 1开启 2停用
  createdAt: number;
  updatedAt: number;
  menuIds?: string;
}

export interface GetRoleListParams {
  name?: string;
  startTime?: number;
  endTime?: number;
  page?: number;
  pageSize?: number;
}

export interface CreateRoleParams {
  name: string;
  status: number;
  menuIds: string;
}

export interface UpdateRoleParams extends CreateRoleParams {
  id: number;
}

export interface RoleMenuDataItem {
  id: number;
  pid: number;
  name: string;
  children?: RoleMenuDataItem[];
}

export interface DeleteRoleParams {
  id: number;
}

export interface LogDataItem {
  id: number;
  adminId: number;
  adminName: string;
  operatedId?: number;
  operatedObject?: string;
  // operatedKeywords?: string; // 搜索字段
  // adminKeywords?: string; // 搜索字段
  subjectType: string; // 平台、一级代理、二级代理、商户
  module: string; // 角色管理、管理员列表、商户管理
  action: string; // 添加管理员、分配角色、编辑角色等
  ip: string;
  createdAt: number;
  before?: string; // 变更前
  after?: string; // 操作后
}

export interface SysUserDataItem {
  id: number;
  appid: string;
  userName: string; // 账号
  nickName: string; // 姓名
  token: string;
  userType: number;
  userTypeId: number;
  roleId: number;
  sysRoleIds: string; // 角色ID，逗号分割
  status: number; // 状态
  remark: string;
  isSuper: boolean;
  createdAt: number;
  updatedAt: number;
  loginIp: string; // 最后登录IP
  loginTime: number; // 最后登录时间
  googleSecret: string;
  googleUrl: string;
  isOpenGoogleCheck: boolean;
  contactInfo: string; // 联系方式
  roleNames?: string[]; // 补充角色名称数组，方便前端展示
}

export interface OperationModuleItem {
  id: number;
  name: string;
  [property: string]: any;
}

export interface GetSysUserListParams {
  keywords?: string; // 搜索账号/昵称/ID
  startTime?: number;
  endTime?: number;
  page?: number;
  size?: number;
}

export interface CreateSysUserParams {
  userName: string;
  nickName: string;
  password?: string;
  contactInfo?: string;
  sysRoleIds: string;
  status: number;
  remark?: string;
}

export interface UpdateSysUserParams extends Partial<CreateSysUserParams> {
  id: number;
}

export interface DeleteSysUserParams {
  id: number;
}

export interface GetLogListParams {
  startTime?: number; // 10位秒级时间戳
  endTime?: number; // 10位秒级时间戳
  adminKeywords?: string; // 搜索管理员ID或名称
  operatedKeywords?: string;
  module?: string;
  subjectType?: string; // 主体类型筛选: 1平台 2一级代理 3二级代理 4商户
  page?: number;
  size?: number;
}

export interface DeleteLogParams {
  id: number;
}

export interface UpdatePasswordParams {
  oldPassword: string;
  newPassword: string;
  verifyToken: string;
}

/** 获取菜单列表 GET /api/v1/sys/listMenu */
export async function getMenuList(params?: any) {
  return request<MenuDataItem[]>("/api/v1/sys/listMenu", {
    method: "GET",
    params,
  });
}

/** 修改密码 POST /api/v1/sys/updatePassword */
export async function updatePassword(data: UpdatePasswordParams) {
  return request<any>("/api/v1/sys/updatePassword", {
    method: "POST",
    data,
  });
}

/** 获取操作日志列表 GET /api/v1/sys/listOperationLog */
export async function getLogList(params: GetLogListParams) {
  return request<{ data: LogDataItem[]; total: number }>(
    "/api/v1/sys/listOperationLog",
    {
      method: "GET",
      params,
    },
  );
}

/** 新增菜单 POST /api/v1/sys/createMenu */
export async function addMenu(data: CreateMenuParams) {
  return request<any>("/api/v1/sys/createMenu", {
    method: "POST",
    data,
  });
}

/** 更新菜单 POST /api/v1/sys/updateMenu */
export async function updateMenu(data: UpdateMenuParams) {
  return request<any>("/api/v1/sys/updateMenu", {
    method: "POST",
    data,
  });
}

/** 删除菜单 POST /api/v1/sys/deleteMenu */
export async function deleteMenu(data: DeleteMenuParams) {
  return request<any>("/api/v1/sys/deleteMenu", {
    method: "POST",
    data,
  });
}

/** 获取角色列表 GET /api/v1/sys/listRole */
export async function getRoleList(params: GetRoleListParams) {
  return request<{ list: RoleDataItem[]; total: number }>(
    "/api/v1/sys/listRole",
    {
      method: "GET",
      params,
    },
  );
}

/** 获取角色可选择菜单 GET /api/v1/sys/roleMenu */
export async function getRoleMenu() {
  return request<RoleMenuDataItem[]>("/api/v1/sys/roleMenu", {
    method: "GET",
  });
}

/** 创建角色 POST /api/v1/sys/createRole */
export async function addRole(data: CreateRoleParams) {
  return request<any>("/api/v1/sys/createRole", {
    method: "POST",
    data,
  });
}

/** 编辑角色 POST /api/v1/sys/updateRole */
export async function updateRole(data: UpdateRoleParams) {
  return request<any>("/api/v1/sys/updateRole", {
    method: "POST",
    data,
  });
}

/** 删除角色 POST /api/v1/sys/deleteRole */
export async function deleteRole(data: DeleteRoleParams) {
  return request<any>("/api/v1/sys/deleteRole", {
    method: "POST",
    data,
  });
}

/** 获取管理员列表 GET /api/v1/sys/listSysUser */
export async function listSysUser(params: GetSysUserListParams) {
  return request<{ list: SysUserDataItem[]; total: number }>(
    "/api/v1/sys/listSysUser",
    {
      method: "GET",
      params,
    },
  );
}

/** 创建管理员 POST /api/v1/sys/createSysUser */
export async function createSysUser(data: CreateSysUserParams) {
  return request<any>("/api/v1/sys/createSysUser", {
    method: "POST",
    data,
  });
}

/** 更新管理员 POST /api/v1/sys/updateSysUser */
export async function updateSysUser(data: UpdateSysUserParams) {
  return request<any>("/api/v1/sys/updateSysUser", {
    method: "POST",
    data,
  });
}

/** 获取操作日志模块列表 GET /api/v1/sys/listOperationModule */
export async function listOperationModule() {
  return request<{ list: OperationModuleItem[] }>(
    "/api/v1/sys/listOperationModule",
    {
      method: "GET",
    },
  );
}

/** 删除管理员 POST /api/v1/sys/deleteSysUser */
export async function deleteSysUser(data: DeleteSysUserParams) {
  return request<any>("/api/v1/sys/deleteSysUser", {
    method: "POST",
    data,
  });
}

// ============ 公告管理 ============

export interface AnnouncementItem {
  id?: number;
  /** 标题 */
  title?: string;
  /** 英文标题 */
  titleEn?: string;
  /** 内容 */
  content?: string;
  /** 英文内容 */
  contentEn?: string;
  /** 公告类型：1版本更新 2系统公告 */
  type?: number;
  /** 可见层级：1平台 2一级代理 3二级代理 4商户，逗号分隔 */
  visibleLevels?: string;
  /** 创建时间 */
  createdAt?: number;
  /** 更新时间 */
  updatedAt?: number;
  [property: string]: any;
}

export interface ListAnnouncementParams {
  /** 开始时间 */
  startTime?: number;
  /** 结束时间 */
  endTime?: number;
  /** 公告类型：1版本更新 2系统公告 */
  type?: string;
  page?: number;
  pageSize?: number;
  [property: string]: any;
}

export interface CreateAnnouncementParams {
  /** 标题 */
  title: string;
  /** 英文标题 */
  titleEn?: string;
  /** 内容 */
  content: string;
  /** 英文内容 */
  contentEn?: string;
  /** 公告类型：1版本更新 2系统公告 */
  type: number;
  /** 可见层级：1平台 2一级代理 3二级代理 4商户，逗号分隔 */
  visibleLevels?: string;
  [property: string]: any;
}

export interface UpdateAnnouncementParams extends CreateAnnouncementParams {
  id: number;
}

export interface DeleteAnnouncementParams {
  id: number;
}

/** 获取公告列表 GET /api/v1/sys/listAnnouncement */
export async function listAnnouncement(params: ListAnnouncementParams) {
  return request<{ list: AnnouncementItem[]; totalCount: number }>(
    "/api/v1/sys/listAnnouncement",
    {
      method: "GET",
      params,
    },
  );
}

/** 新增公告 POST /api/v1/sys/createAnnouncement */
export async function createAnnouncement(data: CreateAnnouncementParams) {
  return request<any>("/api/v1/sys/createAnnouncement", {
    method: "POST",
    data,
  });
}

/** 编辑公告 POST /api/v1/sys/updateAnnouncement */
export async function updateAnnouncement(data: UpdateAnnouncementParams) {
  return request<any>("/api/v1/sys/updateAnnouncement", {
    method: "POST",
    data,
  });
}

/** 删除公告 POST /api/v1/sys/deleteAnnouncement */
export async function deleteAnnouncement(data: DeleteAnnouncementParams) {
  return request<any>("/api/v1/sys/deleteAnnouncement", {
    method: "POST",
    data,
  });
}

// ============ 个人信息 ============

export interface OperatorInfo {
  /** 代理层级 2一级代理 3二级代理 4普通商户 */
  agentLevel: number;
  /** 商户唯一标识 */
  appid: string;
  /** 绑定上级时间 */
  bindTime: number;
  /** 联系方式 */
  contactInformation: string;
  /** 创建时间 */
  createdAt: number;
  /** 商户服务地址 */
  endPoint: string;
  /** 手续费 分成比例 x% */
  fee: number;
  /** 谷歌身份验证秘钥 */
  googleSecret: string;
  /** 谷歌身份验证秘钥url */
  googleUrl: string;
  /** 主键id */
  id: number;
  /** ip列表 */
  ipList: string;
  /** 是否打开谷歌身份验证1是开启0是默认 */
  isOpenGoogleCheck: number;
  /** 登录ip */
  loginIp: string;
  /** 上次登录时间 */
  loginTime: number;
  /** 负责人 */
  operatorManagerName: string;
  /** 商户名称 */
  operatorName: string;
  /** 上级代理的id 0为没有上级/上级是平台 */
  pid: number;
  /** 商户公钥 */
  publicKey: string;
  /** 服务器地区 */
  region?: string;
  /** 备注 */
  remark: string;
  /** 0正常,1冻结 */
  status: number;
  /** 时区 */
  timeZone: string;
  /** 用户的token */
  token: string;
  /** 解绑时间 */
  unbindTime: number;
  /** 更新时间 */
  updatedAt: number;
  /** 用户名 */
  userName: string;
  /** 0=单一钱包模式,1=转账模式 */
  walletMode: string;
}

export interface GetOperatorParams {
  /** 商户id userInfo里面的operatorId */
  id: number;
}

/** 获取运营商个人信息 GET /api/v1/sys/getOperator */
export async function getOperator(params: GetOperatorParams) {
  return request<OperatorInfo>("/api/v1/sys/getOperator", {
    method: "GET",
    params,
  });
}
